import { Buffer } from 'buffer';
import { AvailableIntegration } from '@bageltechnologies/bagel-common';

interface URLGeneratorConfig {
  subdomain?: string;
  redirectUri: string;
  runtimeConfig: Record<string, string>;
  clientId?: string;
  clientSecret?: string;
}

/**
 * Resolves environment variables from the runtime configuration
 */
const resolveEnvVar = (value: string, runtimeConfig: Record<string, string>): string => {
  const envVarMatch = value.match(/\${process\.env\.(.+)}/);
  if (!envVarMatch) return value;
  
  const configKey = envVarMatch[1];
  return runtimeConfig[configKey] || '';
};

/**
 * Processes subdomain for integrations that require it
 */
const processSubdomain = (integration: AvailableIntegration, subdomain: string): string => {
  const { oauth } = integration;
  
  if (!oauth?.authorizationUrl) {
    // If no template provided, return subdomain as is
    return subdomain;
  }

  // TODO: Handle test domain cases (this is only for salesforce sandbox account)
  // const isTestDomain = oauth.testDomain ? subdomain.includes(oauth.testDomain) : false;
  // const uriPostfix = isTestDomain && oauth.uriPostfix
  //   ? oauth.uriPostfix.replace('my.', '')
  //   : oauth.uriPostfix;

  return oauth.authorizationUrl.replace('{subdomain}', subdomain)
};

/**
 * Generates the base authorization URL for the integration
 */
const getBaseOAuthUrl = (
  integration: AvailableIntegration,
  config: URLGeneratorConfig
): string => {
  const { oauth } = integration;
  if (!oauth) {
    throw new Error(`No OAuth configuration found for ${integration.name}`);
  }
  if (integration.key === "salesforce" && config.subdomain === "test") {
    return "https://test.salesforce.com/services/oauth2/authorize"
  }
  // Handle subdomain-based URLs
  if (oauth.requiresSubdomain && config.subdomain) {
    return processSubdomain(integration, config.subdomain);
  }
  
  // Use standard authorization URL
  return oauth.authorizationUrl || '';
};

/**
 * Processes and returns OAuth scopes
 */
const getScopes = (integration: AvailableIntegration): string => {
  // const defaultScopes = integration.oauth?.defaultScopes || [];
  //TODO: Add Support for required / optional scopes
  // const requiredScopes = integration.oauth?.requiredScopes || [];
  const configuredScopes = integration.oauth?.scopes || [];
  
  // Combine all scopes, with required scopes taking precedence
  // const allScopes = Array.from(new Set([...defaultScopes, ...configuredScopes, ...requiredScopes]));
  // return configuredScopes.map(scope => resolveEnvVar(scope, runtimeConfig));

  return configuredScopes.join(' ');
};

/**
 * Generates state parameter with metadata
 */
const generateState = (integration: AvailableIntegration, subdomain?: string): string => {
  const stateData = {
    integrationType: integration.key,
    ...integration.oauth?.metadata,
    ...(subdomain && { subdomain })
  };
  
  return Buffer.from(JSON.stringify(stateData)).toString('base64');
};

/**
 * Main function to generate OAuth URL
 */
export const generateOAuthUrl = (
  integration: AvailableIntegration,
  config: URLGeneratorConfig
): string => {
  try {
    // Get base authorization URL
    if (!integration.oauth) {
      throw new Error(`No OAuth configuration found for ${integration.name}`);
    }
    const baseUrl = getBaseOAuthUrl(integration, config);
    if (!baseUrl) {
      throw new Error(`No authorization URL available for ${integration.name}`);
    }
    let clientId
    if (integration.oauth?.manuallySetIdSecret) {
      // Resolve client ID from environment
      clientId = config.clientId || config.runtimeConfig[`REACT_APP_${integration.oauth.clientId}`]
      if (!clientId) {
        throw new Error(`Missing client ID configuration for ${integration.name}`);
      }
    } else {
      clientId = config.runtimeConfig[`REACT_APP_${integration.oauth.clientId}`]
    }

    // Build standard OAuth parameters
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: config.redirectUri,
      response_type: 'code',
      state: generateState(integration, config.subdomain)
    });

    // Add scopes if present
    const scopes = getScopes(integration);
    if (scopes.length > 0) {
      params.append('scope', scopes);
    }
    // Add any additional parameters specified in the integration config
    if (integration.oauth.additionalParams) {
      Object.entries(integration.oauth.additionalParams).forEach(([key, value]) => {
        params.append(key, resolveEnvVar(value, config.runtimeConfig));
      });
    }

    // Handle custom auth flows if specified
    // if (integration.metadata?.customAuthFlow) {
    //   params.append('response_mode', 'query');
    // }

    // Construct final URL
    return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${params.toString()}`;
  } catch (error) {
    console.error('Error generating OAuth URL:', error);
    throw error;
  }
};

/**
 * Validates subdomain if required by the integration
 */
export const validateSubdomain = (
  integration: AvailableIntegration,
  subdomain: string
): { isValid: boolean; error?: string } => {
  const { oauth } = integration;
  
  if (!oauth?.requiresSubdomain) {
    return { isValid: true };
  }

  if (!subdomain) {
    return { 
      isValid: false, 
      error: 'Subdomain is required' 
    };
  }
  //TODO: Add subdomain validation
  //const validator = oauth?.subdomainValidation;
  //if (!validator) {
  //  return { isValid: true };
  //}

  // For now, we only support validateSalesforceURL
  // This can be expanded to support other validators
  // if (validator.validator === 'validateSalesforceURL') {
  //   // This should be imported from your validation utils
  //   const isValid = true; // validateSalesforceURL(subdomain);
  //   return {
  //     isValid,
  //     error: isValid ? undefined : validator.errorMessage
  //   };
  // }

  return { isValid: true };
}; 